import PlusIcon from "../../../../components/Icons/PlusIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./FBTBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    SubscriptionSelector,
    Swatches,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, FBTConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getPixelsForImage,
    updateBundleConfigWithRealTimeProducts,
    getFbtProductRecommendations,
    ProductRecommendationIntent,
    AvailableFBTTemplates,
    buildMetafieldFromSwatchOptions,
    IMAGE_DISPLAY_OPT,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import { hexToRgba } from "../../../utilities";
import ProductSubscriptionsPreview from "../../BundlePreviewWrapper/ProductSubscriptionsPreview";
import { FbtAiData } from "./FbtAiData";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId } from "../../../constants";

function isProductAvailable(product: ProductObj) {
    if (!product || !product.variants) {
        return false;
    }
    return product?.variants?.some(
        (variant) => (variant.quantityAvailable && variant.quantityAvailable > 0) || variant.availableForSale,
    );
}

// ── Fetch all store products using Storefront API ──
// FIX: price and compareAtPrice are MoneyV2 objects — must use { amount currencyCode }
async function fetchAllStoreProducts(
    sft: string,
    shopDomain: string,
): Promise<Array<{ id: string; title: string; product: any }>> {
    const query = `{
        products(first: 50) {
            edges {
                node {
                    id
                    title
                    handle
                    tags
                    images(first: 1) {
                        edges { node { url altText } }
                    }
                    variants(first: 10) {
                        edges {
                            node {
                                id
                                title
                                availableForSale
                                quantityAvailable
                                selectedOptions { name value }
                                image { url }
                                price {
                                    amount
                                    currencyCode
                                }
                                compareAtPrice {
                                    amount
                                    currencyCode
                                }
                            }
                        }
                    }
                    options { id name values }
                }
            }
        }
    }`;

    const res = await fetch(`https://${shopDomain}/api/2026-01/graphql.json`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": sft,
        },
        body: JSON.stringify({ query }),
    });

    if (!res.ok) throw new Error(`Storefront API HTTP error: ${res.status}`);

    const json = await res.json();

    if (json?.errors) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
    }

    const edges = json?.data?.products?.edges || [];

    return edges.map((e: any) => {
        const n = e.node;
        const variants = n.variants.edges.map((ve: any) => {
            const v = ve.node;
            // price is MoneyV2 { amount, currencyCode } — extract just the amount string
            const priceAmount = v.price?.amount ?? "0";
            const compareAtPriceAmount = v.compareAtPrice?.amount ?? priceAmount;

            return {
                id: v.id,
                title: v.title,
                displayName: v.title,
                // The rest of the app expects price as a plain numeric string (e.g. "29.99")
                price: priceAmount,
                compareAtPrice: compareAtPriceAmount,
                availableForSale: v.availableForSale,
                quantityAvailable: v.quantityAvailable,
                selectedOptions: v.selectedOptions,
                image: v.image ? { originalSrc: v.image.url } : null,
                required_product: true,
                weight: 0,
                weight_unit: "POUNDS",
                selling_plan: "",
                copyPrice: priceAmount,
            };
        });

        const product = {
            id: n.id,
            title: n.title,
            handle: n.handle,
            tags: n.tags || [],
            images: n.images.edges.map((ie: any) => ({
                id: ie.node.url,
                originalSrc: ie.node.url,
                altText: ie.node.altText || "",
            })),
            variants,
            options: n.options,
            quantity: 1,
            maxProductStock: 50,
            selectedVariants: variants.length > 0 ? [variants[0]] : [],
            subscriptions: [],
            oneTimePurchase: true,
            isSubscribed: false,
            alternateProducts: [],
        };

        return { id: n.id, title: n.title, product };
    });
}

const FBTBundle = (bundleConfig: FBTConfigInterface) => {
    return (
        !!bundleConfig && (
            <FBTPreview
                bundleConfig={bundleConfig}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                    } as TStoreContext
                }
                previewType={null}
            />
        )
    );
};

type Props_FBTPreview = {
    bundleConfig: FBTConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
    previewType: any;
    inspectorMode?: boolean;
};

const ProductCardShimmer = ({ brandingVisible }: { brandingVisible: boolean }) => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "20px",
                color: "#999999",
                paddingRight: 10,
            }}
        >
            <span style={{ marginTop: brandingVisible ? -30 : 0 }}>Select product(s)</span>
        </div>
    );
};

const VerticalOfferedProducts = ({
    offeredProducts,
    bundleConfig,
    finalProductsList,
    appConfigContext,
    shouldDiscountApply,
    setFinalProductsList,
    standAlone,
    isMobile,
    inspectorMode = false,
}: {
    offeredProducts: ProductObj[];
    bundleConfig: FBTConfigInterface;
    finalProductsList: ProductObj[];
    appConfigContext: any;
    shouldDiscountApply: any;
    setFinalProductsList: any;
    standAlone: boolean;
    isMobile: boolean;
    inspectorMode?: boolean;
}) => {
    return offeredProducts?.length ? (
        <>
            <div
                style={{
                    borderRadius: "50%",
                    margin: "auto",
                    background: `${hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.7)}`,
                    maxHeight: 20,
                    minHeight: 20,
                    maxWidth: 20,
                    minWidth: 20,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="plusIcon">
                    <span style={{ display: "flex", transform: "scale(1.5)" }}>
                        <PlusIcon color={"white"} />
                    </span>
                </InspectorEditableBlock>
            </div>
            <div
                style={{
                    border: `${bundleConfig?.bundles_design?.border_width}px solid ${hexToRgba(
                        bundleConfig?.bundles_design?.product_border_color,
                        0.2,
                    )}`,
                    borderRadius: bundleConfig?.bundles_design?.border_radius,
                    backgroundColor: bundleConfig?.bundles_design.product_background_color,
                }}
            >
                {offeredProducts.map((product: any, index: number) => (
                    <ProductCardNew
                        key={product?.id}
                        index={index + 1}
                        product={product}
                        bundleConfig={bundleConfig}
                        appConfigContext={appConfigContext}
                        standAlone={standAlone}
                        isMobile={isMobile}
                        setFinalProductsList={setFinalProductsList}
                        finalProductsList={finalProductsList}
                        shouldDiscountApply={shouldDiscountApply}
                        mainProduct={false}
                        inspectorMode={inspectorMode}
                    />
                ))}
            </div>
        </>
    ) : (
        <></>
    );
};

const HorizontalOfferedProducts = ({
    offeredProducts,
    bundleConfig,
    finalProductsList,
    appConfigContext,
    shouldDiscountApply,
    setFinalProductsList,
    standAlone,
    isMobile,
    inspectorMode = false,
}: {
    offeredProducts: ProductObj[];
    bundleConfig: FBTConfigInterface;
    finalProductsList: ProductObj[];
    appConfigContext: any;
    shouldDiscountApply: any;
    setFinalProductsList: any;
    standAlone: boolean;
    isMobile: boolean;
    inspectorMode?: boolean;
}) => {
    return offeredProducts?.length ? (
        <div style={{ display: "flex", width: "100%", gap: isMobile ? "5px" : "10px" }}>
            <div
                style={{
                    borderRadius: "50%",
                    margin: "auto",
                    background: `${hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.7)}`,
                    maxHeight: 20,
                    minHeight: 20,
                    maxWidth: 20,
                    minWidth: 20,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <span style={{ display: "flex", transform: "scale(1.5)" }}>
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="plusIcon">
                        <PlusIcon color={"white"} />
                    </InspectorEditableBlock>
                </span>
            </div>
            <div
                style={{
                    border: `${bundleConfig?.bundles_design?.border_width}px solid ${hexToRgba(
                        bundleConfig?.bundles_design?.product_border_color,
                        0.2,
                    )}`,
                    borderRadius: bundleConfig?.bundles_design?.border_radius,
                    backgroundColor: bundleConfig?.bundles_design.product_background_color,
                    display: "flex",
                    padding: 12,
                    gap: 5,
                }}
            >
                {offeredProducts.map((product: any, index: number) => (
                    <ProductCard
                        key={product?.id}
                        index={index + 1}
                        product={product}
                        bundleConfig={bundleConfig}
                        appConfigContext={appConfigContext}
                        standAlone={standAlone}
                        isMobile={isMobile}
                        setFinalProductsList={setFinalProductsList}
                        finalProductsList={finalProductsList}
                        shouldDiscountApply={shouldDiscountApply}
                        mainProduct={false}
                        inspectorMode={inspectorMode}
                    />
                ))}
            </div>
        </div>
    ) : (
        <></>
    );
};

const AiProductCards = ({
    bundleConfig,
    appConfigContext,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
    isMobile,
    inspectorMode = false,
}: {
    bundleConfig: any;
    appConfigContext: TStoreContext;
    finalProductsList: any;
    setFinalProductsList: any;
    shouldDiscountApply: boolean;
    isMobile: any;
    inspectorMode?: boolean;
}) => {
    const { sft } = appConfigContext;
    const [fetchingAiFbt, setFetchingAiFbt] = useState(true);

    const arr = finalProductsList?.filter((rec: any, idx: number) => idx == 0);
    const offeredProducts = finalProductsList?.filter(
        (rec: any, idx: number) => idx > 0 && idx < bundleConfig?.ai_fbt_product_limit,
    );

    const getAiFbtData = async (_sft: string) => {
        try {
            setFetchingAiFbt(true);
            const initialData = await updateBundleConfigWithRealTimeProducts(_sft, [bundleConfig], false, 1);
            if (initialData?.length > 0 && initialData[0]?.products?.length) {
                const firstProductId = initialData[0].products[0].id;
                const fbtData = await getFbtProductRecommendations(
                    _sft,
                    firstProductId,
                    bundleConfig,
                    6,
                    false,
                    ProductRecommendationIntent.RELATED,
                );
                await updateBundleConfigWithRealTimeProducts(_sft, [fbtData as any], false, null);
                const mainProducts = fbtData?.products || [];
                const offeredProducts = fbtData?.offered_products || [];
                if (mainProducts.length > 0 || offeredProducts.length > 0) {
                    const finalProducts = [...mainProducts, ...offeredProducts].map((product, idx) => ({
                        ...product,
                        checked:
                            idx > 0 && bundleConfig?.no_preselect_offered_products
                                ? false
                                : isProductAvailable(product as any),
                        required_product: mainProducts.includes(product),
                        quantity: product?.quantity || 1,
                    }));
                    setFinalProductsList(finalProducts);
                    return;
                }
            }
            const fallbackProducts = (FbtAiData || []).map((product, idx) => ({
                ...product,
                checked:
                    idx > 0 && bundleConfig?.no_preselect_offered_products ? false : isProductAvailable(product as any),
                required_product: idx == 0 ? true : false,
            }));
            setFinalProductsList(fallbackProducts);
        } catch (error) {
            console.error("Error fetching AI FBT data:", error);
        } finally {
            setFetchingAiFbt(false);
        }
    };

    useEffect(() => {
        if (bundleConfig?.fbt_product_selection == "all") {
            getAiFbtData(sft);
        }
    }, [sft, bundleConfig?.fbt_product_selection, bundleConfig?.no_preselect_offered_products]);

    return bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL ? (
        <div
            className={`${styles.products} ${styles.productsOverflow}`}
            style={{ flexWrap: "nowrap", flexDirection: "column", width: "100%", gap: "5px" }}
        >
            {fetchingAiFbt ? (
                <div className={styles.card}>...</div>
            ) : (
                <>
                    {arr.map((product: any, index: number) => (
                        <ProductCardNew
                            key={product?.id}
                            index={index}
                            product={product}
                            bundleConfig={bundleConfig}
                            appConfigContext={appConfigContext}
                            standAlone={false}
                            isMobile={isMobile}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={arr}
                            shouldDiscountApply={shouldDiscountApply}
                            mainProduct={true}
                            inspectorMode={inspectorMode}
                        />
                    ))}
                    <VerticalOfferedProducts
                        offeredProducts={offeredProducts}
                        bundleConfig={bundleConfig}
                        setFinalProductsList={setFinalProductsList}
                        finalProductsList={finalProductsList}
                        shouldDiscountApply={shouldDiscountApply}
                        appConfigContext={appConfigContext}
                        standAlone={false}
                        isMobile={isMobile}
                        inspectorMode={inspectorMode}
                    />
                </>
            )}
        </div>
    ) : (
        /* FIX: Outer scroll wrapper constrains width, inner div has max-content */
        <div className={styles.productsScrollWrapper}>
            <div
                className={`${styles.products} ${isMobile ? styles.productsOverflow : styles.productsFBThorizontal}`}
                style={{ flexWrap: "nowrap", paddingTop: 0, ...(isMobile && { gap: "5px" }) }}
            >
                {fetchingAiFbt ? (
                    <div className={styles.card}>...</div>
                ) : (
                    <>
                        {arr.map((product: any, index: number) => (
                            <ProductCard
                                key={product?.id}
                                index={index}
                                product={product}
                                bundleConfig={bundleConfig}
                                appConfigContext={appConfigContext}
                                standAlone={false}
                                isMobile={isMobile}
                                setFinalProductsList={setFinalProductsList}
                                finalProductsList={arr}
                                shouldDiscountApply={shouldDiscountApply}
                                mainProduct={true}
                                inspectorMode={inspectorMode}
                            />
                        ))}
                        <HorizontalOfferedProducts
                            offeredProducts={offeredProducts}
                            bundleConfig={bundleConfig}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={finalProductsList}
                            shouldDiscountApply={shouldDiscountApply}
                            appConfigContext={appConfigContext}
                            standAlone={false}
                            isMobile={isMobile}
                            inspectorMode={inspectorMode}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// ── Shared dropdown ──
const PreviewProductDropdown = ({
    label,
    products,
    selectedId,
    onSelect,
    disabled = false,
}: {
    label: string;
    products: Array<{ id: string; title: string }>;
    selectedId: string;
    onSelect: (id: string) => void;
    disabled?: boolean;
}) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            padding: "6px 10px",
            background: "#f9f9f9",
            borderRadius: 6,
            border: "1px solid #e5e5e5",
        }}
    >
        <span style={{ fontSize: 13, color: "#444", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</span>
        <select
            value={selectedId}
            onChange={(e) => onSelect(e.target.value)}
            disabled={disabled}
            style={{
                flex: 1,
                fontSize: 13,
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid #ddd",
                background: "white",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                maxHeight: "200px",
                overflowY: "auto",
            }}
        >
            {products.map((p) => (
                <option key={p.id} value={p.id}>
                    {p.title}
                </option>
            ))}
        </select>
    </div>
);

// ── Manual + All Products admin preview ──
const ManualAllProductCards = ({
    bundleConfig,
    appConfigContext,
    setFinalProductsList,
    shouldDiscountApply,
    isMobile,
    inspectorMode = false,
    standAlone = false,
}: {
    bundleConfig: FBTConfigInterface;
    appConfigContext: TStoreContext;
    setFinalProductsList: any;
    shouldDiscountApply: boolean;
    isMobile: boolean;
    inspectorMode?: boolean;
    standAlone?: boolean;
}) => {
    const sft: string = standAlone ? window.kefi_bundles.sft : (appConfigContext as any)?.sft || "";
    const shopDomain: any = standAlone ? window.kefi_bundles.shop : shopify.config.shop;

    const [storeProducts, setStoreProducts] = useState<Array<{ id: string; title: string; product: any }>>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [selectedMainProductId, setSelectedMainProductId] = useState<string>("");
    const [hydratedMainProduct, setHydratedMainProduct] = useState<any>(null);

    useEffect(() => {
        if (!sft || !shopDomain) {
            setFetchError(`Config error: sft=${sft ? "ok" : "missing"}, shop=${shopDomain || "missing"}`);
            setLoadingProducts(false);
            return;
        }

        const offeredIds = new Set((bundleConfig?.offered_products || []).map((p: any) => p.id));

        setLoadingProducts(true);
        setFetchError(null);

        fetchAllStoreProducts(sft, shopDomain)
            .then((all) => {
                const filtered = all.filter((p) => !offeredIds.has(p.id));
                setStoreProducts(filtered);
                if (filtered.length > 0) {
                    setSelectedMainProductId(filtered[0].id);
                }
            })
            .catch((err) => {
                console.error("ManualAllProductCards fetch error:", err);
                setFetchError(`Failed to load products: ${err.message}`);
            })
            .finally(() => {
                setLoadingProducts(false);
            });
    }, [sft, shopDomain]);

    useEffect(() => {
        if (!selectedMainProductId || !storeProducts.length) {
            setHydratedMainProduct(null);
            return;
        }
        const found = storeProducts.find((p) => p.id === selectedMainProductId);
        if (!found) return;
        setHydratedMainProduct({
            ...found.product,
            checked: isProductAvailable(found.product),
            required_product: true,
            quantity: found.product.quantity || 1,
        });
    }, [selectedMainProductId, storeProducts]);

    const offeredProducts = useMemo(
        () =>
            (bundleConfig?.offered_products || [])
                .filter((p: any) => p.id !== selectedMainProductId) // <--- Filter out the main product
                .map((p: any) => ({
                    ...p,
                    checked: bundleConfig?.no_preselect_offered_products ? false : isProductAvailable(p),
                    required_product: false,
                    quantity: p?.quantity || 1,
                })),
        [bundleConfig?.offered_products, bundleConfig?.no_preselect_offered_products, selectedMainProductId],
    );

    const previewList = useMemo(() => {
        if (!hydratedMainProduct) return offeredProducts;
        return [hydratedMainProduct, ...offeredProducts];
    }, [hydratedMainProduct, offeredProducts]);

    useEffect(() => {
        setFinalProductsList(previewList);
    }, [previewList]);

    if (!offeredProducts.length) {
        return (
            <div style={{ width: "100%" }}>
                <PreviewProductDropdown
                    label="Product:"
                    products={storeProducts}
                    selectedId={selectedMainProductId}
                    onSelect={(id) => setSelectedMainProductId(id)}
                    disabled={false}
                />
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "14px",
                        color: "#999999",
                        padding: "20px 10px",
                        textAlign: "center",
                    }}
                >
                    <span>Add Suggested products to see preview</span>
                </div>
            </div>
        );
    }

    const previewMainProducts = previewList.filter((p: any) => !!p.required_product);
    const previewOfferedProducts = previewList.filter((p: any) => !p.required_product);

    const dropdownOptions: Array<{ id: string; title: string }> = loadingProducts
        ? [{ id: "__loading__", title: "Loading products…" }]
        : fetchError
          ? [{ id: "__error__", title: "Error loading products" }]
          : storeProducts.length === 0
            ? [{ id: "__empty__", title: "No products found" }]
            : storeProducts;

    const dropdownDisabled = loadingProducts || !!fetchError || storeProducts.length === 0;

    return (
        <div style={{ width: "100%" }}>
            <PreviewProductDropdown
                label="Product:"
                products={dropdownOptions}
                selectedId={
                    loadingProducts || fetchError
                        ? dropdownOptions[0]?.id
                        : selectedMainProductId || dropdownOptions[0]?.id
                }
                onSelect={(id) => {
                    if (!dropdownDisabled) setSelectedMainProductId(id);
                }}
                disabled={dropdownDisabled}
            />

            {/* Error detail below dropdown — shown only on error */}
            {fetchError && (
                <div style={{ textAlign: "center", padding: "10px", color: "#c00", fontSize: 12, marginBottom: 8 }}>
                    {fetchError}
                </div>
            )}

            {loadingProducts ? (
                <div style={{ textAlign: "center", padding: "20px 10px", color: "#aaa", fontSize: 13 }}>
                    Loading products…
                </div>
            ) : fetchError ? null : !hydratedMainProduct ? (
                <div style={{ textAlign: "center", padding: "20px 10px", color: "#aaa", fontSize: 13 }}>
                    Loading preview…
                </div>
            ) : bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL ? (
                <div
                    className={`${styles.products} ${styles.productsOverflow}`}
                    style={{ flexWrap: "nowrap", flexDirection: "column", width: "100%", gap: "5px" }}
                >
                    {previewMainProducts.map((product: any, index: number) => (
                        <ProductCardNew
                            key={product?.id}
                            index={index}
                            product={product}
                            bundleConfig={bundleConfig}
                            appConfigContext={appConfigContext}
                            standAlone={false}
                            isMobile={isMobile}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={previewList}
                            shouldDiscountApply={shouldDiscountApply}
                            mainProduct={true}
                            inspectorMode={inspectorMode}
                        />
                    ))}
                    {previewOfferedProducts.length > 0 && (
                        <VerticalOfferedProducts
                            offeredProducts={previewOfferedProducts}
                            bundleConfig={bundleConfig}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={previewList}
                            shouldDiscountApply={shouldDiscountApply}
                            appConfigContext={appConfigContext}
                            standAlone={false}
                            isMobile={isMobile}
                            inspectorMode={inspectorMode}
                        />
                    )}
                </div>
            ) : (
                /* FIX: Scroll wrapper below product images, above footer */
                <div className={styles.productsScrollWrapper}>
                    <div
                        className={`${styles.products} ${isMobile ? styles.productsOverflow : styles.productsFBThorizontal}`}
                        style={{ flexWrap: "nowrap", paddingTop: 0, ...(isMobile && { gap: "5px" }) }}
                    >
                        {previewMainProducts.map((product: any, index: number) => (
                            <ProductCard
                                key={product?.id}
                                index={index}
                                product={product}
                                bundleConfig={bundleConfig}
                                appConfigContext={appConfigContext}
                                standAlone={false}
                                isMobile={isMobile}
                                setFinalProductsList={setFinalProductsList}
                                finalProductsList={previewList}
                                shouldDiscountApply={shouldDiscountApply}
                                mainProduct={true}
                                inspectorMode={inspectorMode}
                            />
                        ))}
                        {previewOfferedProducts.length > 0 && (
                            <HorizontalOfferedProducts
                                offeredProducts={previewOfferedProducts}
                                bundleConfig={bundleConfig}
                                setFinalProductsList={setFinalProductsList}
                                finalProductsList={previewList}
                                shouldDiscountApply={shouldDiscountApply}
                                appConfigContext={appConfigContext}
                                standAlone={false}
                                isMobile={isMobile}
                                inspectorMode={inspectorMode}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Manual + Specific Products preview ──
const ManualSpecificPreviewSelector = ({
    bundleConfig,
    appConfigContext,
    isMobile,
    inspectorMode,
    shouldDiscountApply,
    setFinalProductsList,
    standAlone,
}: {
    bundleConfig: FBTConfigInterface;
    appConfigContext: TStoreContext;
    isMobile: boolean;
    inspectorMode: boolean;
    shouldDiscountApply: boolean;
    setFinalProductsList: any;
    standAlone: boolean;
}) => {
    const mainProducts = bundleConfig?.products || [];
    const offeredProducts = bundleConfig?.offered_products || [];

    const [selectedMainProductId, setSelectedMainProductId] = useState<string>(mainProducts[0]?.id || "");

    const selectedMain = mainProducts.find((p: any) => p.id === selectedMainProductId) || mainProducts[0];
    const validOffered = offeredProducts.filter((op: any) => op.id !== selectedMain?.id);

    const previewList = useMemo(() => {
        if (!selectedMain) return [];
        return [
            {
                ...selectedMain,
                checked: isProductAvailable(selectedMain as any),
                required_product: true,
                quantity: selectedMain?.quantity || 1,
            },
            ...validOffered.map((p: any) => ({
                ...p,
                checked: bundleConfig?.no_preselect_offered_products ? false : isProductAvailable(p),
                required_product: false,
                quantity: p?.quantity || 1,
            })),
        ];
    }, [
        selectedMainProductId,
        bundleConfig?.products,
        bundleConfig?.offered_products,
        bundleConfig?.no_preselect_offered_products,
    ]);

    useEffect(() => {
        setFinalProductsList(previewList);
    }, [previewList]);

    const previewOfferedProducts = previewList.filter((p: any) => !p.required_product);
    const previewMainProducts = previewList.filter((p: any) => !!p.required_product);
    const showDropdown = mainProducts.length > 1;

    return (
        <div style={{ width: "100%" }}>
            {showDropdown && (
                <PreviewProductDropdown
                    label="Product:"
                    products={mainProducts.map((p: any) => ({ id: p.id, title: p.title }))}
                    selectedId={selectedMainProductId}
                    onSelect={setSelectedMainProductId}
                />
            )}

            {previewList.length === 0 ? (
                <ProductCardShimmer brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi} />
            ) : bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL ? (
                <div
                    className={`${styles.products} ${styles.productsOverflow}`}
                    style={{ flexDirection: "column", width: "100%", gap: "5px" }}
                >
                    {previewMainProducts.map((product: any, index: number) => (
                        <ProductCardNew
                            key={product?.id}
                            index={index}
                            product={product}
                            bundleConfig={bundleConfig}
                            appConfigContext={appConfigContext}
                            standAlone={standAlone}
                            isMobile={isMobile}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={previewList}
                            shouldDiscountApply={shouldDiscountApply}
                            mainProduct={true}
                            inspectorMode={inspectorMode}
                        />
                    ))}
                    {previewOfferedProducts.length > 0 && (
                        <VerticalOfferedProducts
                            offeredProducts={previewOfferedProducts}
                            bundleConfig={bundleConfig}
                            setFinalProductsList={setFinalProductsList}
                            finalProductsList={previewList}
                            shouldDiscountApply={shouldDiscountApply}
                            appConfigContext={appConfigContext}
                            standAlone={standAlone}
                            isMobile={isMobile}
                            inspectorMode={inspectorMode}
                        />
                    )}
                </div>
            ) : (
                /* FIX: Scroll wrapper below product images, above footer */
                <div className={styles.productsScrollWrapper}>
                    <div
                        className={`${styles.products} ${isMobile ? styles.productsOverflow : styles.productsFBThorizontal}`}
                        style={{ paddingTop: 0, ...(isMobile && { gap: "5px" }) }}
                    >
                        {previewMainProducts.map((product: any, index: number) => (
                            <ProductCard
                                key={product?.id}
                                index={index}
                                product={product}
                                bundleConfig={bundleConfig}
                                appConfigContext={appConfigContext}
                                standAlone={standAlone}
                                isMobile={isMobile}
                                setFinalProductsList={setFinalProductsList}
                                finalProductsList={previewList}
                                shouldDiscountApply={shouldDiscountApply}
                                mainProduct={true}
                                inspectorMode={inspectorMode}
                            />
                        ))}
                        {previewOfferedProducts.length > 0 && (
                            <HorizontalOfferedProducts
                                offeredProducts={previewOfferedProducts}
                                bundleConfig={bundleConfig}
                                setFinalProductsList={setFinalProductsList}
                                finalProductsList={previewList}
                                shouldDiscountApply={shouldDiscountApply}
                                appConfigContext={appConfigContext}
                                standAlone={standAlone}
                                isMobile={isMobile}
                                inspectorMode={inspectorMode}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ProductCardNew = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    isMobile,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
    mainProduct,
    inspectorMode = false,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: FBTConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    isMobile: boolean;
    setFinalProductsList: any;
    finalProductsList: any[] | [];
    shouldDiscountApply: boolean;
    mainProduct: boolean;
    inspectorMode?: boolean;
}) => {
    const [disPrice, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(product.quantity)
            ?.fill(product?.variants?.find((vv) => vv.availableForSale == true) || product?.variants?.[0])
            ?.map((variant) => ({
                ...variant,
                selling_plan: !product.oneTimePurchase
                    ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                    : "",
                copyPrice: variant?.price,
            })),
    );
    const { currency, money_format } = appConfigContext;

    useEffect(() => {
        if (product?.variants?.length) {
            let disP = +product.variants[0].price * product?.quantity;
            const origP = +product.variants[0].price * product?.quantity;
            const dis = bundleConfig?.discount_value || 0;
            switch (bundleConfig?.discount_type) {
                case "percentage":
                    disP = disP - disP * (dis / 100);
                    break;
                default:
            }
            setDisPrice(Math.round(disP * 100) / 100);
            setOrigPrice(Math.round(origP * 100) / 100);
            setSelectedVariants(
                new Array(product.quantity)
                    .fill(product.variants.find((vv) => vv.availableForSale == true) || product.variants[0])
                    .map((variant) => ({
                        ...variant,
                        selling_plan: !product.oneTimePurchase
                            ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                            : "",
                        copyPrice: variant.price,
                    })),
            );
        } else {
            setDisPrice(0);
            setOrigPrice(0);
        }
    }, [product.id, product.quantity, bundleConfig?.discount_type, bundleConfig?.discount_value]);

    useEffect(() => {
        if (product.checked) {
            const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
                ...variant,
                productIdx: index,
            }));
            product.selectedVariants = updatedSelectedVariants;
            setFinalProductsList((ps: ProductObj[]) => {
                const updated = [...ps];
                if (updated[index]) {
                    updated[index] = { ...updated[index], selectedVariants: updatedSelectedVariants };
                }
                return updated;
            });
            let totalOriginalPrice = 0;
            let totalDiscountedPrice = 0;
            updatedSelectedVariants.forEach((variant: any) => {
                if (product.checked) {
                    const variantPrice = bundleConfig?.apply_discount_on_compare_price
                        ? +variant?.compareAtPrice
                        : +variant?.price;
                    totalOriginalPrice += variantPrice;
                }
            });
            totalDiscountedPrice = totalOriginalPrice;
            const discountValue = bundleConfig?.discount_value || 0;
            switch (bundleConfig?.discount_type) {
                case "percentage":
                    totalDiscountedPrice = totalOriginalPrice - totalOriginalPrice * (discountValue / 100);
                    break;
                default:
                    break;
            }
            setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
            setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
        }
    }, [selectedVariants, product.checked, bundleConfig, index, bundleConfig?.apply_discount_on_compare_price]);

    const handleProductClick = (e: any) => {
        e?.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
        }
    };

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const isProductAvailableMemo = useMemo(() => {
        return product?.variants?.some(
            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [product]);

    const getPrice = (type: any, price: any) => {
        const finalPrice = getFinalPrice(type, 0, price);
        return finalPrice <= 0 ? bundleConfig.free_text || "Free" : getPriceLabel(finalPrice, money_format, currency);
    };

    const getNewPriceLabel = () => {
        const discountedPrice = getFinalPrice("none", 0, disPrice);
        const compareAtPrice = getFinalPrice(
            "none",
            0,
            bundleConfig.apply_discount_on_compare_price || bundleConfig?.use_compare_at_price
                ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
                : origPrice,
        );
        if (discountedPrice < compareAtPrice) {
            return getPriceLabel(compareAtPrice, money_format, currency);
        }
        if (!product.checked) return null;
        return null;
    };

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({ product, bundleConfig: bundleConfig || {} });

    const renderPrice = (
        <div
            style={{
                width: "max-content",
                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "row",
                alignItems: "baseline",
                gap: 5,
                position: "relative",
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div style={{ width: "max-content" }}>
                    {bundleConfig?.discount_type === "percentage"
                        ? getPrice("none", shouldDiscountApply && !!product.checked ? disPrice : origPrice)
                        : getPrice("none", disPrice)}
                </div>
            </InspectorEditableBlock>
            {((shouldDiscountApply && product.checked) || (!product.checked && bundleConfig.use_compare_at_price)) && (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                    <div
                        style={{
                            textDecoration: "line-through",
                            fontSize: bundleConfig?.bundles_design.product_price_size,
                            color: bundleConfig?.bundles_design.product_price_color,
                            fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                            width: "max-content",
                            lineHeight: "normal",
                        }}
                    >
                        {getNewPriceLabel()}
                    </div>
                </InspectorEditableBlock>
            )}
        </div>
    );

    return (
        <>
            {!mainProduct && index > 1 && (
                <div style={{ margin: "0px 10px", display: "flex", justifyContent: "center" }}>
                    <span className={styles.separator}></span>
                </div>
            )}
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product?.tags?.toString()}
                productId={getProductId(product?.id)}
            >
                <div
                    style={{
                        flexDirection: "column",
                        padding: "10px",
                        ...(mainProduct && {
                            border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            backgroundColor: bundleConfig?.bundles_design.product_background_color,
                        }),
                    }}
                >
                    <div className={styles.productCardContainer}>
                        <div className={styles.productInfoContainer}>
                            <ProductImageWrapper
                                product={product}
                                handleProductClick={handleProductClick}
                                bundleConfig={{
                                    ...bundleConfig,
                                    bundles_design: { ...bundleConfig?.bundles_design, product_img_border_width: 0 },
                                }}
                                isMobile={isMobile}
                                selectedVariants={selectedVariants}
                            />
                            <div
                                className={styles.productMetaRight}
                                style={{
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    gap: isMobile
                                        ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") /
                                              10 +
                                          "px"
                                        : "0px",
                                    whiteSpace: "normal",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: bundleConfig?.bundles_design.product_name_size,
                                        color: bundleConfig?.bundles_design?.product_name_color,
                                        fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        gap: 10,
                                    }}
                                    onClick={handleProductClick}
                                >
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey={"productTitle"}
                                        wrapWithDiv={true}
                                    >
                                        <div
                                            style={{
                                                maxWidth: "200px",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: inspectorMode ? "" : "hidden",
                                                textOverflow: "ellipsis",
                                                lineHeight: bundleConfig?.bundles_design.product_name_size + 2 + "px",
                                            }}
                                            title={product?.title}
                                        >
                                            {product?.title}
                                        </div>
                                    </InspectorEditableBlock>
                                    <>
                                        {isProductAvailableMemo ? (
                                            renderPrice
                                        ) : (
                                            <span style={{ fontSize: "12px", color: "red" }}>
                                                {bundleConfig?.bundles_settings?.out_of_stock_text}
                                            </span>
                                        )}
                                    </>
                                </div>
                                {product.variants.length > 1 && (
                                    <div
                                        style={{ display: "flex", flexDirection: "column", gap: "10px", width: "85%" }}
                                    >
                                        {selectedVariants.map((_: any, i: number) =>
                                            userShopname === "typsy-beauty.myshopify.com" ? (
                                                <Swatches
                                                    bundlesDesign={bundleConfig?.bundles_design}
                                                    key={i}
                                                    variants={product.variants}
                                                    handleChange={setSelectedVariants}
                                                    selected={selectedVariants}
                                                    options={product.options || []}
                                                    productCardIndex={i}
                                                    standAlone={standAlone}
                                                />
                                            ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                                <CustomSwatches
                                                    key={i}
                                                    variants={product.variants}
                                                    handleChange={setSelectedVariants}
                                                    selected={selectedVariants}
                                                    options={product.options || []}
                                                    productCardIndex={i}
                                                    standAlone={standAlone}
                                                    metafield={effectiveMetafield}
                                                    bundlesDesign={bundleConfig?.bundles_design}
                                                    bundleConfig={bundleConfig}
                                                />
                                            ) : (
                                                <VariantSelectorV2
                                                    key={i}
                                                    variants={product.variants}
                                                    handleChange={setSelectedVariants}
                                                    selected={selectedVariants}
                                                    options={product.options || []}
                                                    productCardIndex={i}
                                                    standAlone={standAlone}
                                                    metafield={effectiveMetafield}
                                                    bundlesDesign={bundleConfig?.bundles_design}
                                                    bundleConfig={bundleConfig}
                                                />
                                            ),
                                        )}
                                    </div>
                                )}
                                {isMobile && (
                                    <ProductSubscriptionsPreview
                                        product={product}
                                        setSelectedVariants={setSelectedVariants}
                                        key={product.id}
                                        bundleConfig={bundleConfig}
                                    />
                                )}
                            </div>
                        </div>
                        {((index > 0 && bundleConfig?.fbt_show_checkboxes == 1) ||
                            (index == 0 && !bundleConfig?.prevent_deselect_main_product)) && (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCheckbox">
                                <input
                                    id={styles.fbtInputCheckbox2}
                                    style={{ accentColor: bundleConfig?.bundles_design?.add_to_cart_bg_color }}
                                    type={"checkbox"}
                                    disabled={!isProductAvailableMemo}
                                    key={product.id}
                                    name={product.title}
                                    value={product.id}
                                    onChange={(e) => {
                                        const currentCheck = e.currentTarget.checked;
                                        const currentVal = e.target.value;
                                        setFinalProductsList((ps: ProductObj[]) =>
                                            ps.map((fpl: any) =>
                                                fpl.id == currentVal ? { ...fpl, checked: currentCheck } : fpl,
                                            ),
                                        );
                                    }}
                                    checked={
                                        !!finalProductsList?.find((fpl: ProductObj) => fpl?.id == product?.id)?.checked
                                    }
                                />
                            </InspectorEditableBlock>
                        )}
                    </div>
                    {product.isSubscribed && (
                        <div style={{ width: "100%" }}>
                            <div style={{ margin: "5px 0" }}>
                                <span className={styles.separator}></span>
                            </div>
                            <SubscriptionSelector
                                product={product}
                                selectedVariants={selectedVariants}
                                setSelectedVariants={setSelectedVariants}
                            />
                        </div>
                    )}
                </div>
            </InspectorEditableBlock>
        </>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    isMobile,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
    mainProduct,
    inspectorMode = false,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: FBTConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    isMobile: boolean;
    setFinalProductsList: any;
    finalProductsList: any[] | [];
    shouldDiscountApply: boolean;
    mainProduct: boolean;
    inspectorMode?: boolean;
}) => {
    const [disPrice, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(product.quantity)
            ?.fill(product?.variants?.find((vv) => vv.availableForSale == true) || product?.variants?.[0])
            ?.map((variant) => ({
                ...variant,
                selling_plan: !product.oneTimePurchase
                    ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                    : "",
                copyPrice: variant?.price,
            })),
    );
    const { currency, money_format } = appConfigContext;

    useEffect(() => {
        if (product?.variants?.length) {
            let disP = +product.variants[0].price * product?.quantity;
            const origP = +product.variants[0].price * product?.quantity;
            const dis = bundleConfig?.discount_value || 0;
            switch (bundleConfig?.discount_type) {
                case "percentage":
                    disP = disP - disP * (dis / 100);
                    break;
                default:
            }
            setDisPrice(Math.round(disP * 100) / 100);
            setOrigPrice(Math.round(origP * 100) / 100);
            setSelectedVariants(
                new Array(product.quantity)
                    .fill(product.variants.find((vv) => vv.availableForSale == true) || product.variants[0])
                    .map((variant) => ({
                        ...variant,
                        selling_plan: !product.oneTimePurchase
                            ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                            : "",
                        copyPrice: variant.price,
                    })),
            );
        } else {
            setDisPrice(0);
            setOrigPrice(0);
        }
    }, [product.id, product.quantity, bundleConfig?.discount_type, bundleConfig?.discount_value]);

    useEffect(() => {
        if (product.checked) {
            const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
                ...variant,
                productIdx: index,
            }));
            product.selectedVariants = updatedSelectedVariants;
            setFinalProductsList((ps: ProductObj[]) => {
                const updated = [...ps];
                if (updated[index]) {
                    updated[index] = { ...updated[index], selectedVariants: updatedSelectedVariants };
                }
                return updated;
            });
            let totalOriginalPrice = 0;
            let totalDiscountedPrice = 0;
            updatedSelectedVariants.forEach((variant: any) => {
                if (product.checked) {
                    const variantPrice = bundleConfig?.apply_discount_on_compare_price
                        ? +variant?.compareAtPrice
                        : +variant?.price;
                    totalOriginalPrice += variantPrice;
                }
            });
            totalDiscountedPrice = totalOriginalPrice;
            const discountValue = bundleConfig?.discount_value || 0;
            switch (bundleConfig?.discount_type) {
                case "percentage":
                    totalDiscountedPrice = totalOriginalPrice - totalOriginalPrice * (discountValue / 100);
                    break;
                default:
                    break;
            }
            setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
            setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
        }
    }, [selectedVariants, product.checked, bundleConfig, index, bundleConfig?.apply_discount_on_compare_price]);

    const handleProductClick = (e: any) => {
        e?.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
        }
    };

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const isProductAvailableMemo = useMemo(() => {
        return product?.variants?.some(
            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [product]);

    const getPrice = (type: any, price: any) => {
        const finalPrice = getFinalPrice(type, 0, price);
        return finalPrice <= 0 ? bundleConfig.free_text || "Free" : getPriceLabel(finalPrice, money_format, currency);
    };

    const getNewPriceLabel = () => {
        const discountedPrice = getFinalPrice("none", 0, disPrice);
        const compareAtPrice = getFinalPrice(
            "none",
            0,
            bundleConfig.apply_discount_on_compare_price || bundleConfig?.use_compare_at_price
                ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
                : origPrice,
        );
        if (discountedPrice < compareAtPrice) {
            return getPriceLabel(compareAtPrice, money_format, currency);
        }
        if (!product.checked) return null;
        return null;
    };

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({ product, bundleConfig: bundleConfig || {} });

    const renderPrice = (
        <div
            style={{
                width: "max-content",
                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "column",
                alignItems:
                    bundleConfig?.selected_template_fbt === AvailableFBTTemplates.HORIZONTAL
                        ? "flex-start"
                        : "flex-end",
                gap: 3,
                position: "relative",
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div style={{ width: "max-content" }}>
                    {bundleConfig?.discount_type === "percentage"
                        ? getPrice("none", shouldDiscountApply && !!product.checked ? disPrice : origPrice)
                        : getPrice("none", disPrice)}
                </div>
            </InspectorEditableBlock>
            {((shouldDiscountApply && product.checked) || (!product.checked && bundleConfig.use_compare_at_price)) && (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                    <div
                        style={{
                            textDecoration: "line-through",
                            fontSize: bundleConfig?.bundles_design.product_price_size,
                            color: bundleConfig?.bundles_design.product_price_color,
                            fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                            width: "max-content",
                            lineHeight: "normal",
                        }}
                    >
                        {getNewPriceLabel()}
                    </div>
                </InspectorEditableBlock>
            )}
        </div>
    );

    return (
        <>
            {!mainProduct && index > 1 && (
                <div
                    style={{
                        margin: "0px 10px",
                        display: "flex",
                        justifyContent: "center",
                        height: "95%",
                        alignSelf: "center",
                        alignItems: "center",
                        width: 1,
                        background: hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.2),
                    }}
                >
                    <span className={styles.separator}></span>
                </div>
            )}
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product?.tags?.toString()}
                productId={getProductId(product?.id)}
            >
                <div
                    className={styles.productWrapper}
                    style={{
                        maxWidth:
                            +getPixelsForImage(
                                bundleConfig?.bundles_design.product_img_size
                                    ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                    : "fbt_small",
                                "number",
                            ) + (mainProduct ? 26 : 0),
                        ...(((isMobile &&
                            bundleConfig?.bundles_design?.product_img_show == IMAGE_DISPLAY_OPT.DESKTOP) ||
                            bundleConfig?.bundles_design?.product_img_show == IMAGE_DISPLAY_OPT.NONE) && {
                            minWidth:
                                +getPixelsForImage(
                                    bundleConfig?.bundles_design.product_img_size
                                        ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                        : "fbt_small",
                                    "number",
                                ) + 26,
                        }),
                        ...(mainProduct
                            ? {
                                  border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                  borderRadius: bundleConfig?.bundles_design?.border_radius,
                                  backgroundColor: bundleConfig?.bundles_design.product_background_color,
                              }
                            : { padding: 0 }),
                        gap:
                            bundleConfig?.selected_template_fbt === AvailableFBTTemplates.HORIZONTAL
                                ? "10px"
                                : undefined,
                    }}
                >
                    <div className={styles.productInfoRight}>
                        {((index > 0 && bundleConfig?.fbt_show_checkboxes == 1) ||
                            (index == 0 && !bundleConfig?.prevent_deselect_main_product)) && (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCheckbox">
                                <input
                                    id={styles.fbtInputCheckbox}
                                    style={{ accentColor: bundleConfig?.bundles_design?.add_to_cart_bg_color }}
                                    type={"checkbox"}
                                    disabled={!isProductAvailableMemo}
                                    key={product.id}
                                    name={product.title}
                                    value={product.id}
                                    onChange={(e) => {
                                        const currentCheck = e.currentTarget.checked;
                                        const currentVal = e.target.value;
                                        setFinalProductsList((ps: ProductObj[]) =>
                                            ps.map((fpl: any) =>
                                                fpl.id == currentVal ? { ...fpl, checked: currentCheck } : fpl,
                                            ),
                                        );
                                    }}
                                    checked={
                                        !!finalProductsList?.find((fpl: ProductObj) => fpl?.id == product?.id)?.checked
                                    }
                                />
                            </InspectorEditableBlock>
                        )}
                        <ProductImageWrapper
                            product={product}
                            handleProductClick={handleProductClick}
                            bundleConfig={{
                                ...bundleConfig,
                                bundles_design: {
                                    ...bundleConfig?.bundles_design,
                                    product_img_border_width: 0,
                                    product_img_size: bundleConfig?.bundles_design.product_img_size
                                        ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                        : "fbt_small",
                                },
                            }}
                            isMobile={isMobile}
                            selectedVariants={selectedVariants}
                        />
                        <div
                            className={styles.productMetaRight}
                            style={{
                                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                gap: isMobile
                                    ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") /
                                          10 +
                                      "px"
                                    : "0px",
                                whiteSpace: "normal",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: bundleConfig?.bundles_design.product_name_size,
                                    color: bundleConfig?.bundles_design?.product_name_color,
                                    fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                    cursor: "pointer",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    gap: 10,
                                }}
                                onClick={handleProductClick}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productTitle"
                                    wrapWithDiv={true}
                                >
                                    <div
                                        style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: inspectorMode ? "" : "hidden",
                                            textOverflow: "ellipsis",
                                            lineHeight: bundleConfig?.bundles_design.product_name_size + 2 + "px",
                                        }}
                                        title={product?.title}
                                    >
                                        {product?.title}
                                    </div>
                                </InspectorEditableBlock>
                                {!isMobile && (
                                    <>
                                        {isProductAvailableMemo ? (
                                            renderPrice
                                        ) : (
                                            <span style={{ fontSize: "12px", color: "red" }}>
                                                {bundleConfig?.bundles_settings?.out_of_stock_text}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className={styles.subInfo}>
                                {isMobile &&
                                    (isProductAvailableMemo ? (
                                        renderPrice
                                    ) : (
                                        <span style={{ fontSize: "12px", color: "red" }}>
                                            {bundleConfig?.bundles_settings?.out_of_stock_text}
                                        </span>
                                    ))}
                            </div>
                            {isMobile && (
                                <ProductSubscriptionsPreview
                                    product={product}
                                    setSelectedVariants={setSelectedVariants}
                                    key={product.id}
                                    bundleConfig={bundleConfig}
                                />
                            )}
                        </div>
                    </div>
                    {product.variants.length > 1 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%",
                                maxWidth: "90%",
                            }}
                        >
                            {selectedVariants.map((_: any, i: number) =>
                                userShopname === "typsy-beauty.myshopify.com" ? (
                                    <Swatches
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                    />
                                ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                    <CustomSwatches
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                        metafield={effectiveMetafield}
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        bundleConfig={bundleConfig}
                                        maxWidth={
                                            +getPixelsForImage(
                                                bundleConfig?.bundles_design.product_img_size
                                                    ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                                    : "fbt_small",
                                                "number",
                                            )
                                        }
                                    />
                                ) : (
                                    <VariantSelectorV2
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                        metafield={effectiveMetafield}
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        bundleConfig={bundleConfig}
                                        maxWidth={
                                            +getPixelsForImage(
                                                bundleConfig?.bundles_design.product_img_size
                                                    ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                                    : "fbt_small",
                                                "number",
                                            )
                                        }
                                    />
                                ),
                            )}
                        </div>
                    )}
                    {product.isSubscribed && (
                        <div style={{ width: "100%" }}>
                            <div style={{ margin: "5px 0" }}>
                                <span className={styles.separator}></span>
                            </div>
                            <SubscriptionSelector
                                product={product}
                                selectedVariants={selectedVariants}
                                setSelectedVariants={setSelectedVariants}
                            />
                        </div>
                    )}
                </div>
            </InspectorEditableBlock>
        </>
    );
};

export const FBTPreview = ({
    bundleConfig,
    standAlone,
    appConfigContext,
    previewType,
    inspectorMode = false,
}: Props_FBTPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const { currency, money_format } = appConfigContext;
    const bundleRef = useRef(null);
    const { isMobile: _isMobile } = useIsMobile();
    const isMobile = previewType ? (previewType === 1 ? true : false) : _isMobile;
    const [shouldDiscountApply, setShouldDiscountApply] = useState(true);
    const [finalProductsList, setFinalProductsList] = useState<any[] | []>([]);

    const isAiMode = bundleConfig?.fbt_product_selection == "all";
    const isManualAll =
        bundleConfig?.fbt_product_selection == "manual" && (bundleConfig as any)?.manual_product_scope == "all";
    const isManualSpecific =
        bundleConfig?.fbt_product_selection == "manual" && (bundleConfig as any)?.manual_product_scope !== "all";

    useEffect(() => {
        if (finalProductsList && finalProductsList.length) {
            let total = 0;
            let comp = 0;
            finalProductsList.forEach((product) => {
                if (product.checked && product.selectedVariants) {
                    product.selectedVariants.forEach((variant: any) => {
                        const variantPrice = bundleConfig?.apply_discount_on_compare_price
                            ? +variant?.compareAtPrice
                            : +variant?.price;
                        total += variantPrice;
                        comp += +variant?.compareAtPrice;
                    });
                }
            });
            setTotalPrice(total);
            setCompareAtPrice(comp);
        } else {
            setTotalPrice(0);
            setCompareAtPrice(0);
        }
    }, [
        finalProductsList,
        bundleConfig?.discount_type,
        bundleConfig?.discount_value,
        bundleConfig?.apply_discount_on_compare_price,
        bundleConfig?.use_compare_at_price,
    ]);

    useEffect(() => {
        let isRequiredChecked = false;
        let isNonRequiredChecked = false;
        finalProductsList?.forEach((pd, idx: number) => {
            if (idx === 0 && !standAlone) {
                window.localStorage.setItem("fbt_handle", pd.handle);
            }
            if (pd.required_product && pd.checked) isRequiredChecked = true;
            if (!pd.required_product && pd.checked) isNonRequiredChecked = true;
        });
        setShouldDiscountApply(isRequiredChecked && isNonRequiredChecked);
    }, [finalProductsList, standAlone]);

    useEffect(() => {
        // FIX: Only skip for admin preview (non-standAlone) for manual modes.
        // On storefront (standAlone=true), we must build finalProductsList for all modes.
        if (!standAlone && (isManualAll || isManualSpecific)) return;
        if ((bundleConfig?.products?.length || 0) > 0 || (bundleConfig?.offered_products?.length || 0) > 0) {
            const arr = [
                ...(bundleConfig?.products?.map((obj) => ({
                    ...obj,
                    checked: isProductAvailable(obj),
                    required_product: true,
                    quantity: obj?.quantity || 1,
                })) || []),
                ...(bundleConfig?.offered_products?.map((obj) => ({
                    ...obj,
                    checked: bundleConfig?.no_preselect_offered_products ? false : isProductAvailable(obj),
                    required_product: false,
                    quantity: obj?.quantity || 1,
                })) || []),
            ];
            setFinalProductsList(arr);
        } else if (bundleConfig?.fbt_product_selection == "manual") {
            setFinalProductsList([]);
        }
    }, [
        bundleConfig.products,
        bundleConfig?.offered_products,
        bundleConfig?.fbt_product_selection,
        bundleConfig?.no_preselect_offered_products,
    ]);

    const offeredProducts = finalProductsList.filter((product) => !product?.required_product);

    if (!bundleConfig) return null;

    const updatedBundleConfig = {
        ...bundleConfig,
        products: finalProductsList.filter((fpl) => !!fpl.checked),
        discount_type: shouldDiscountApply ? bundleConfig?.discount_type : "none",
        discount_value: shouldDiscountApply ? bundleConfig?.discount_value : "0",
    };

    return (
        <div style={{ overflow: standAlone ? "hidden" : "", display: "flex", width: "100%", flexDirection: "column" }}>
            <BundlePreviewWrapper
                bundleConfig={updatedBundleConfig as any}
                standAlone={standAlone}
                currency={currency}
                money_format={money_format}
                totalPrice={totalPrice}
                compareAtPriceTotal={
                    bundleConfig?.apply_discount_on_compare_price || bundleConfig?.use_compare_at_price
                        ? compareAtPrice
                        : totalPrice
                }
                overrideMobile={isMobile}
            >
                {/* CASE 1: AI Suggestions */}
                {!standAlone && isAiMode ? (
                    <AiProductCards
                        bundleConfig={{
                            ...bundleConfig,
                            form_id: bundleConfig?.form_id || "123",
                            fbt_product_selection: "all",
                        }}
                        appConfigContext={appConfigContext}
                        finalProductsList={finalProductsList}
                        shouldDiscountApply={shouldDiscountApply}
                        setFinalProductsList={setFinalProductsList}
                        isMobile={isMobile}
                        inspectorMode={inspectorMode}
                    />
                ) : /* CASE 2: Manual + All Products */
                !standAlone && isManualAll ? (
                    <ManualAllProductCards
                        bundleConfig={bundleConfig}
                        appConfigContext={appConfigContext}
                        setFinalProductsList={setFinalProductsList}
                        shouldDiscountApply={shouldDiscountApply}
                        isMobile={isMobile}
                        inspectorMode={inspectorMode}
                        standAlone={!!standAlone}
                    />
                ) : /* CASE 3: Manual + Specific Products */
                !standAlone && isManualSpecific ? (
                    <ManualSpecificPreviewSelector
                        bundleConfig={bundleConfig}
                        appConfigContext={appConfigContext}
                        isMobile={isMobile}
                        inspectorMode={inspectorMode}
                        shouldDiscountApply={shouldDiscountApply}
                        setFinalProductsList={setFinalProductsList}
                        standAlone={!!standAlone}
                    />
                ) : finalProductsList?.length ? (
                    /* CASE 4: Storefront — also uses scroll wrapper for horizontal */
                    <>
                        {bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL ? (
                            <div
                                className={`${styles.products} ${styles.productsOverflow}`}
                                ref={bundleRef}
                                style={{ flexDirection: "column", width: "100%", gap: "5px" }}
                            >
                                {finalProductsList
                                    .filter((product) => !!product?.required_product)
                                    ?.map((product, index) => (
                                        <ProductCardNew
                                            key={product?.id + product?.variants[0]?.id}
                                            index={index}
                                            product={product}
                                            bundleConfig={bundleConfig}
                                            appConfigContext={appConfigContext}
                                            standAlone={!!standAlone}
                                            isMobile={isMobile}
                                            setFinalProductsList={setFinalProductsList}
                                            finalProductsList={finalProductsList}
                                            shouldDiscountApply={shouldDiscountApply}
                                            mainProduct={true}
                                            inspectorMode={inspectorMode}
                                        />
                                    ))}
                                <VerticalOfferedProducts
                                    offeredProducts={offeredProducts}
                                    bundleConfig={bundleConfig}
                                    setFinalProductsList={setFinalProductsList}
                                    finalProductsList={finalProductsList}
                                    shouldDiscountApply={shouldDiscountApply}
                                    appConfigContext={appConfigContext}
                                    standAlone={!!standAlone}
                                    isMobile={isMobile}
                                    inspectorMode={inspectorMode}
                                />
                            </div>
                        ) : (
                            /* FIX: Scroll wrapper so scrollbar is below products, above footer */
                            <div className={styles.productsScrollWrapper}>
                                <div
                                    className={`${styles.products} ${isMobile ? styles.productsOverflow : styles.productsFBThorizontal}`}
                                    ref={bundleRef}
                                    style={{ paddingTop: 0, ...(isMobile && { gap: "5px" }) }}
                                >
                                    {finalProductsList
                                        .filter((product) => !!product?.required_product)
                                        ?.map((product, index) => (
                                            <ProductCard
                                                key={product?.id + product?.variants[0]?.id}
                                                index={index}
                                                product={product}
                                                bundleConfig={bundleConfig}
                                                appConfigContext={appConfigContext}
                                                standAlone={!!standAlone}
                                                isMobile={isMobile}
                                                setFinalProductsList={setFinalProductsList}
                                                finalProductsList={finalProductsList}
                                                shouldDiscountApply={shouldDiscountApply}
                                                mainProduct={true}
                                                inspectorMode={inspectorMode}
                                            />
                                        ))}
                                    <HorizontalOfferedProducts
                                        offeredProducts={offeredProducts}
                                        bundleConfig={bundleConfig}
                                        setFinalProductsList={setFinalProductsList}
                                        finalProductsList={finalProductsList}
                                        shouldDiscountApply={shouldDiscountApply}
                                        appConfigContext={appConfigContext}
                                        standAlone={!!standAlone}
                                        isMobile={isMobile}
                                        inspectorMode={inspectorMode}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <ProductCardShimmer brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi} />
                )}
            </BundlePreviewWrapper>
        </div>
    );
};

export default FBTBundle;
